/** @jsxImportSource theme-ui */
import { Box, Text, useThemeUI } from "theme-ui";
import { useSelector } from "react-redux";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { motion } from "framer-motion";
import castArray from "lodash/fp/castArray";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import head from "lodash/fp/head";
import getOr from "lodash/fp/getOr";
import get from "lodash/fp/get";

import { useTranslation } from "hooks";
import {
  selectProductImages,
  selectProductBadgeImages,
  selectUserChoiceValues,
  selectUIVersion,
} from "store/app";
import {
  ImageBadges,
  MagnificationButtons,
  PersonalizationCallOut,
  PersonalizationName,
  SkatePersonalizationCallOut,
  ImageCallOut,
  SvgOrImg,
} from "components";
import { filterByDisplayIf } from "utils";
import * as styles from "./ProductImage.styles";

function Images({ userChoiceValues, images = [] }) {
  if (
    userChoiceValues["Holder"] === "LS Edge" &&
    images[0].url.includes("_Powerfly")
  ) {
    images[0].url = images[0].url.replace("_Powerfly", "_LSEdge");
  }

  return images.map(
    ({ name, type, url, css = {}, component, componentProps = {} }, zIndex) => {
      if (url) {
        return (
          <Box key={name} className={`product-image--${name}`}>
            <SvgOrImg type={type} src={url} sx={{ ...css, zIndex }} />
          </Box>
        );
      }

      switch (component) {
        case "PersonalizationCallOut":
          return (
            <PersonalizationCallOut
              key={name + zIndex}
              zIndex={zIndex}
              {...componentProps}
            />
          );

        case "SkatePersonalizationCallOut":
          return (
            <SkatePersonalizationCallOut
              key={name + zIndex}
              zIndex={zIndex}
              {...componentProps}
            />
          );

        case "ImageCallOut":
          return (
            <ImageCallOut
              key={name + zIndex}
              zIndex={zIndex}
              {...componentProps}
            />
          );

        case "PersonalizationName":
          return (
            <PersonalizationName
              key={name + zIndex}
              zIndex={zIndex}
              {...componentProps}
            />
          );

        default:
          console.warn(`ProductImage component missing: ${component}`);
          return null;
      }
    }
  );
}

function ImageGridContainer({
  productImages,
  UIVersion,
  filterByURL,
  isReviewOrderPage,
  userChoiceValues,
}) {
  const numberOfItems = Object.keys(productImages).length;

  const shouldShowDoubleWidthColumn =
    userChoiceValues["customise-pads"] && numberOfItems === 2;

  return (
    <Box
      sx={styles.gridContainer({
        UIVersion,
        numberOfItems,
        isReviewOrderPage,
        doubleWidthColumnOverride: shouldShowDoubleWidthColumn,
      })}
    >
      {productImages.map(({ imageSet, images }, index) => {
        return (
          <Box
            sx={styles.individualImageContainer({ UIVersion })}
            className={`imageGroup--${index}`}
            key={imageSet}
          >
            {
              <Images
                userChoiceValues={userChoiceValues}
                images={images.filter(
                  ({ url }) => !filterByURL || Boolean(url)
                )}
              />
            }
          </Box>
        );
      })}
    </Box>
  );
}

function ZoomInstructions() {
  const { t } = useTranslation();
  const {
    theme: { isDesktop },
  } = useThemeUI();

  return (
    <Box sx={styles.zoomInstructions}>
      {isDesktop && (
        <Text variant="h5">
          {t("Scroll to Zoom")}{" "}
          <span style={{ verticalAlign: "text-bottom" }}>|</span> {t("Click")}
          <span style={{ verticalAlign: "text-bottom" }}> + </span>
          {t("Drag to Pan")}
        </Text>
      )}
      {!isDesktop && <Text variant="h5">{t("Pinch to Zoom")}</Text>}
    </Box>
  );
}

export default function ProductImage({
  minScale = 0.5,
  maxScale = 2,
  withPinchZoom,
  ...props
}) {
  const {
    theme: { isDesktop },
  } = useThemeUI();

  const userChoiceValues = useSelector(selectUserChoiceValues);
  const productImages = useSelector(selectProductImages);
  const productBadgeImages = useSelector(selectProductBadgeImages);
  const selectedImageAngle = useSelector(get(["app", "selectedImageAngle"]));
  const UIVersion = useSelector(selectUIVersion);

  const getCss = flow(
    castArray,
    filter((aPosition) =>
      filterByDisplayIf({
        ...userChoiceValues,
        selectedImageAngle: selectedImageAngle,
      })(aPosition.displayIf)
    ),
    head,
    getOr({}, "css")
  );

  if (!withPinchZoom) {
    if (props.isReviewOrderPage) {
      return (
        <ImageGridContainer
          productImages={productImages}
          UIVersion={UIVersion}
          filterByURL={!withPinchZoom}
          isReviewOrderPage={props.isReviewOrderPage}
          userChoiceValues={userChoiceValues}
        />
      );
    }

    return (
      <Box sx={styles.productImagesContainer} {...props}>
        {!isDesktop && productBadgeImages.length > 0 && (
          <Box sx={styles.imageBadgesContainer}>
            <ImageBadges imageBadges={productBadgeImages} />
          </Box>
        )}

        <ImageGridContainer
          productImages={productImages}
          UIVersion={UIVersion}
          filterByURL={!withPinchZoom}
          userChoiceValues={userChoiceValues}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.productImagesContainer} {...props}>
      <TransformWrapper
        maxScale={maxScale}
        minScale={minScale}
        initialScale={1}
        wheel={{
          disabled: true,
        }}
      >
        {({ zoomIn, zoomOut, state }) => {
          return (
            <>
              {isDesktop && (
                <motion.div
                  sx={styles.controls}
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                    transition: {
                      duration: 0.3,
                      delay: 0.2,
                    },
                  }}
                >
                  <MagnificationButtons
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                    zoomInDisabled={state.scale === maxScale}
                    zoomOutDisabled={state.scale === minScale}
                  />
                </motion.div>
              )}

              {UIVersion === 2 && <ZoomInstructions />}

              <TransformComponent>
                <Box sx={styles.imageGridContainerWrapper}>
                  <ImageGridContainer
                    productImages={productImages}
                    UIVersion={UIVersion}
                    filterByURL={!withPinchZoom}
                    userChoiceValues={userChoiceValues}
                  />
                </Box>

                {productBadgeImages.length > 0 && (
                  <Box
                    sx={styles.imageBadgesInTransform(
                      getCss(productBadgeImages[0].position)[
                        isDesktop ? "desktop" : "mobile"
                      ]
                    )}
                  >
                    <ImageBadges imageBadges={productBadgeImages} />
                  </Box>
                )}
              </TransformComponent>
            </>
          );
        }}
      </TransformWrapper>
    </Box>
  );
}
